import * as M from '@/model';
import deepMerge from '@/util/deep-merge';

export function createRestInterface(root = '/s/rest/') {
  const defaultHeaders = {
    Accept: MediaType.JSON,
    'Content-Type': MediaType.JSON,
    'X-Client-Version': process.env.VUE_APP_BUILD_HASH,
  };
  const errorHandlers: ErrorHandlers = {
    // 401: () => authModule.dispatch.invalidateSession(),
    // 500: (req) => Promise.resolve(notify(this, `Server error on ${req.method} ${req.action}`)),
  };
  return new RestInterfaceImpl({
    url: (action) => root + action,
    root: (action) => `/s/${action}`,
    options: () => ({
      credentials: 'same-origin',
      headers: { ...defaultHeaders },
    }),
    errorHandlers,
  });
}

export enum MediaType {
  JSON = 'application/json',
  TEXT = 'text/plain',
  CSV = 'application/csv; charset=UTF-8',
  WORD = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
}

const binaryContentTypes: string[] = [
  MediaType.CSV, MediaType.WORD,
];

export interface Api {

  session(): Promise<M.Session>;
  oidcConfig(): Promise<M.OIDCConfig>;
  login(token: string): Promise<void>;

  jobs(): Promise<M.SerfJob[]>;
  job(id: number): Promise<M.SerfJob | undefined>;
  manifest(jobId: number): Promise<M.SerfJobManifest | undefined>;
  createJob(manifest: M.SerfJobManifest): Promise<M.SerfJob>;

  documents(): Promise<M.FileDocument[]>;
  documentsFilter(filter: M.DocumentFilter): Promise<M.FileDocument[]>;
  document(id: number): Promise<M.FileDocument | undefined>;
  saveDocument(doc: Partial<M.FileDocument>, translate?: boolean): Promise<M.FileDocument>;
  uploadFile(file: File): Promise<M.FileDescriptor>;
  fileDownloadUri(uuid: M.UUID, forceSave?: boolean): string;
  statusHistory(): Promise<M.FileDocumentStatusHistory[]>;
  documentStatusHistory(documentId: number): Promise<M.FileDocumentStatusHistory[]>;

  ingestDescriptors(documentId: number): Promise<M.IngestDescriptor[]>;
  ingestResult(documentId: number, ingestId?: number): Promise<M.IngestResult>;
  ingestBoxes(boxIds: number[]): Promise<M.IngestBox[]>;

  ingestFragments(ingestId: number): Promise<M.IngestFragment[]>;
  ingestSuggestions(ingestId: number): Promise<M.Suggestion[]>;
  extractedEntities(ingestId: number): Promise<M.ExtractedEntity[]>;
  entityTypes(): Promise<M.EntityType[]>;
  saveFragment(fragment: M.IngestFragment): Promise<M.IngestFragment>;
  deleteFragment(fragmentId: number): Promise<void>;
  saveSuggestion(suggestion: M.Suggestion): Promise<M.Suggestion>;
  fragmentLabelAssignments(ids: number[]): Promise<{ fragmentId: number; labelAssignment: M.LabelAssignment }[]>;

  expandedFragments(fragments: M.IngestTextFragment[]): Promise<M.ExpandedFragment[]>;
  expandedFragmentsById(fragmentIds: number[]): Promise<M.ExpandedFragment[]>;
  originalBoxText(boxId: number): Promise<{ text: string }>;

  documentSummaries(documentId: number, tail?: boolean, spec?: M.SummarySpec): Promise<M.DocumentSummaries>;
  generateDocumentSummaries(documentId: number, spec: M.SummarySpec): Promise<M.DocumentSummary[]>;
  generateDocumentSummariesWithInstructions(documentId: number, spec: M.SummarySpecWithInstructions): Promise<M.DocumentSummary[]>;
  generateBulkDocumentSummaries(documentId: number, spec: M.BulkSummarySpecWithInstructions): Promise<M.SerfJob>;
  getActiveBulkJobs(documentId: number): Promise<M.SerfJob[]>;
  promoteDocumentSummary(documentSummaryId: number, head: boolean): Promise<M.DocumentSummary>;
  approveDocumentSummary(documentSummaryId: number, approve: boolean): Promise<M.DocumentSummary>;
  undoApproveDocumentSummary(documentSummaryId: number): Promise<M.DocumentSummary>;
  editSummary(summaryId: number, text: string): Promise<M.Summary>;
  deleteEditSummary(summaryId: number): Promise<M.Summary>;
  editMotivation(summaryId: number, motivation: string): Promise<M.Summary>;

  query(selection: M.SelectionQuery): Promise<M.SelectionResult>;
  queryChat(query: M.SelectionQuery, messages: M.ChatMessage[]): Promise<M.ChatMessage>;
  queryChatStream(query: M.SelectionQuery, messages: M.ChatMessage[], onToken: (token: string) => void, onStatus?: (status: string) => void): Promise<void>;
  assistantChatStream(messages: M.ChatMessage[], onToken: (token: string) => void, onStatus?: (status: string) => void): Promise<void>;
  queryLabels(selection: M.SelectionQuery): Promise<number[]>;
  contextLabels(labels: number[]): Promise<number[]>;
  labelIdentifiers(selection: M.SelectionQuery): Promise<M.LabelIdentifier[]>;

  potentialLabels(context: M.PotentialLabelContext): Promise<M.PotentialLabelResultItem[]>;
  mlLabelSuggestions(context: M.MlSuggestContext): Promise<M.MlLabelSuggestions>;

  saveQuery(query: M.SavedQuery): Promise<M.SavedQuery>;
  listQueries(): Promise<M.SavedQuery[]>;
  getQuery(id: number): Promise<M.SavedQuery>;

  labels(): Promise<M.Label[]>;
  saveLabel(label: M.Label): Promise<M.Label>;
  labelEdges(): Promise<M.LabelEdge[]>;
  labelUsage(): Promise<Record<number, number>>;
  labelTypes(): Promise<M.LabelType[]>;
  saveLabelType(labelType: M.LabelType): Promise<M.LabelType>;

  users(): Promise<M.User[]>;

  publishers(): Promise<M.Publisher[]>;
  savePublisher(publisher: Partial<M.Publisher>): Promise<M.Publisher>;
  getPublisher(id: number): Promise<M.Publisher>;

  realms(): Promise<M.Realm[]>;
  getRealm(id: number): Promise<M.Realm>;

  sseSubscriptionUrl(): string;

  search(query: string, entity: string): Promise<M.SearchResult[]>;
  semanticSearch(query: string): Promise<M.SearchResult[]>;
  semanticSearchExpand(query: M.SemanticSearchRequest): Promise<M.SemanticSearchResult[]>;

  uiConfig(): Promise<M.UiConfig>;

}

async function streamSse(url: string, body: unknown, onToken: (token: string) => void, onStatus?: (status: string) => void): Promise<void> {
  const response = await fetch(url, {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'Content-Type': MediaType.JSON,
      Accept: 'text/event-stream',
      'X-Client-Version': process.env.VUE_APP_BUILD_HASH as string,
    },
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    throw new Error(`http/${response.status}: ${response.statusText}`);
  }
  const reader = response.body!.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  while (true) {
    // eslint-disable-next-line no-await-in-loop
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop()!;
    for (const line of lines) {
      if (line.startsWith('data: ')) {
        const data = line.slice(6).trim();
        if (data === '[DONE]') return;
        const event = JSON.parse(data) as { type: 'token' | 'status'; text: string };
        if (event.type === 'token') onToken(event.text);
        else if (event.type === 'status' && onStatus) onStatus(event.text);
      }
    }
  }
}

export class ApiImpl implements Api {
  constructor(private rest: RestInterface) {}

  private readonly catchers: { [code: number]: (error: ApiError) => void } = [];

  session(): Promise<M.Session> {
    return this.rest.http(Method.GET, 'session');
  }

  oidcConfig(): Promise<M.OIDCConfig> {
    return this.rest.http(Method.GET, 'session/oidc');
  }

  login(token: string): Promise<void> {
    return this.rest.http(Method.POST, 'session/login', token, { headers: { 'Content-Type': 'text/plain' } });
  }

  jobs(): Promise<M.SerfJob[]> {
    return this.rest.http(Method.GET, 'queue');
  }

  job(id: number): Promise<M.SerfJob | undefined> {
    return this.rest.http(Method.GET, `queue/${id}/job`);
  }

  manifest(jobId: number): Promise<M.SerfJobManifest | undefined> {
    return this.rest.http(Method.GET, `queue/${jobId}/manifest`);
  }

  createJob(manifest: M.SerfJobManifest): Promise<M.SerfJob> {
    return this.rest.http(Method.POST, 'queue', { type: manifest.type, manifest });
  }

  documents(): Promise<M.FileDocument[]> {
    return this.rest.http(Method.GET, 'document');
  }

  documentsFilter(filter: M.DocumentFilter): Promise<M.FileDocument[]> {
    return this.rest.http(Method.POST, 'document', filter);
  }

  document(id: number): Promise<M.FileDocument | undefined> {
    return this.rest.http(Method.GET, `document/${id}`);
  }

  saveDocument(doc: Partial<M.FileDocument>, translate?: boolean): Promise<M.FileDocument> {
    return this.rest.http(Method.PUT, 'document', { doc, translate });
  }

  uploadFile(file: File): Promise<M.FileDescriptor> {
    return this.upload('document/file/upload', [file]);
  }

  fileDownloadUri(uuid: M.UUID, forceSave?: boolean): string {
    return this.rest.getUrl(`document/file/${uuid}/data${forceSave ? '?f=true' : ''}`);
  }

  statusHistory(): Promise<M.FileDocumentStatusHistory[]> {
    return this.rest.http(Method.GET, 'document/status');
  }

  documentStatusHistory(documentId: number): Promise<M.FileDocumentStatusHistory[]> {
    return this.rest.http(Method.GET, `document/${documentId}/status`);
  }

  ingestDescriptors(documentId: number): Promise<M.IngestDescriptor[]> {
    return this.rest.http(Method.GET, `document/${documentId}/ingest/descriptors`);
  }

  ingestResult(documentId: number, ingestId?: number): Promise<M.IngestResult> {
    if (ingestId) {
      return this.rest.http(Method.GET, `document/${documentId}/ingest/${ingestId}`);
    }
    return this.rest.http(Method.GET, `document/${documentId}/ingest`);
  }

  ingestBoxes(boxIds: number[]): Promise<M.IngestBox[]> {
    return this.rest.http(Method.POST, 'document/ingest/boxes', boxIds);
  }

  ingestFragments(ingestId: number): Promise<M.IngestFragment[]> {
    return this.rest.http(Method.GET, `fragment/ingest/${ingestId}`);
  }

  ingestSuggestions(ingestId: number): Promise<M.Suggestion[]> {
    return this.rest.http(Method.GET, `suggestion/ingest/${ingestId}`);
  }

  extractedEntities(ingestId: number): Promise<M.ExtractedEntity[]> {
    return this.rest.http(Method.GET, `entity/extracted/${ingestId}`);
  }

  entityTypes(): Promise<M.EntityType[]> {
    return this.rest.http(Method.GET, 'entity/type');
  }

  fragmentLabelAssignments(ids: number[]): Promise<{ fragmentId: number; labelAssignment: M.LabelAssignment}[]> {
    return this.rest.http(Method.POST, 'fragment/label/assignments', ids);
  }

  expandedFragments(fragments: M.IngestTextFragment[]): Promise<M.ExpandedFragment[]> {
    return this.rest.http(Method.POST, 'fragment/expand', fragments);
  }

  expandedFragmentsById(fragmentIds: number[]): Promise<M.ExpandedFragment[]> {
    return this.rest.http(Method.POST, 'fragment/expand/ids', fragmentIds);
  }

  originalBoxText(boxId: number): Promise<{ text: string }> {
    return this.rest.http(Method.GET, `fragment/box/${boxId}/original`);
  }

  documentSummaries(documentId: number, tail = true, spec?: M.SummarySpec): Promise<M.DocumentSummaries> {
    return this.rest.http(Method.POST, `summary/document/${documentId}?tail=${tail}`, spec);
  }

  generateDocumentSummaries(documentId: number, spec: M.SummarySpec): Promise<M.DocumentSummary[]> {
    return this.rest.http(Method.PUT, `summary/document/${documentId}`, spec);
  }

  generateDocumentSummariesWithInstructions(documentId: number, spec: M.SummarySpecWithInstructions): Promise<M.DocumentSummary[]> {
    return this.rest.http(Method.PUT, `summary/document/${documentId}/instructions`, spec);
  }

  generateBulkDocumentSummaries(documentId: number, spec: M.BulkSummarySpecWithInstructions): Promise<M.SerfJob> {
    return this.rest.http(Method.PUT, `summary/document/${documentId}/bulk`, spec);
  }

  getActiveBulkJobs(documentId: number): Promise<M.SerfJob[]> {
    return this.rest.http(Method.GET, `summary/document/${documentId}/bulk/active`);
  }

  promoteDocumentSummary(documentSummaryId: number, head: boolean): Promise<M.DocumentSummary> {
    return this.rest.http(Method.PUT, `summary/document/summary/${documentSummaryId}/promote`, head);
  }

  approveDocumentSummary(documentSummaryId: number, approve: boolean): Promise<M.DocumentSummary> {
    return this.rest.http(Method.PUT, `summary/document/summary/${documentSummaryId}/approve`, approve);
  }

  undoApproveDocumentSummary(documentSummaryId: number): Promise<M.DocumentSummary> {
    return this.rest.http(Method.DELETE, `summary/document/summary/${documentSummaryId}/approve`);
  }

  editSummary(summaryId: number, text: string): Promise<M.Summary> {
    return this.rest.http(Method.PUT, `summary/${summaryId}/text`, text, { headers: { 'Content-Type': MediaType.TEXT } });
  }

  deleteEditSummary(summaryId: number): Promise<M.Summary> {
    return this.rest.http(Method.DELETE, `summary/${summaryId}/text`);
  }

  editMotivation(summaryId: number, motivation: string): Promise<M.Summary> {
    return this.rest.http(Method.PUT, `summary/${summaryId}/motivation`, motivation, { headers: { 'Content-Type': MediaType.TEXT } });
  }

  query(selection: M.SelectionQuery): Promise<M.SelectionResult> {
    return this.rest.http(Method.POST, 'selection', selection);
  }

  queryChat(query: M.SelectionQuery, messages: M.ChatMessage[]): Promise<M.ChatMessage> {
    return this.rest.http(Method.POST, 'selection/chat', { query, messages });
  }

  async assistantChatStream(messages: M.ChatMessage[], onToken: (token: string) => void, onStatus?: (status: string) => void): Promise<void> {
    await streamSse(this.rest.getUrl('assistant/chat-stream'), { messages }, onToken, onStatus);
  }

  async queryChatStream(query: M.SelectionQuery, messages: M.ChatMessage[], onToken: (token: string) => void, onStatus?: (status: string) => void): Promise<void> {
    await streamSse(this.rest.getUrl('selection/chat-stream'), { query, messages }, onToken, onStatus);
  }

  queryLabels(selection: M.SelectionQuery): Promise<number[]> {
    return this.rest.http(Method.POST, 'selection/label', selection);
  }

  contextLabels(labels: number[]): Promise<number[]> {
    return this.rest.http(Method.POST, 'selection/context', labels);
  }

  labelIdentifiers(selection: M.SelectionQuery): Promise<M.LabelIdentifier[]> {
    return this.rest.http(Method.POST, 'identifier', selection);
  }

  potentialLabels(context: M.PotentialLabelContext): Promise<M.PotentialLabelResultItem[]> {
    return this.rest.http(Method.POST, 'potential', context);
  }

  mlLabelSuggestions(context: M.MlSuggestContext): Promise<M.MlLabelSuggestions> {
    return this.rest.http(Method.POST, 'potential/ml', context);
  }

  saveQuery(query: M.SavedQuery): Promise<M.SavedQuery> {
    return this.rest.http(Method.PUT, 'query/save', query);
  }

  listQueries(): Promise<M.SavedQuery[]> {
    return this.rest.http(Method.GET, 'query/list');
  }

  getQuery(id: number): Promise<M.SavedQuery> {
    return this.rest.http(Method.GET, `query/${id}`);
  }

  saveFragment(fragment: M.IngestFragment): Promise<M.IngestFragment> {
    return this.rest.http(Method.POST, 'fragment', fragment);
  }

  deleteFragment(fragmentId: number): Promise<void> {
    return this.rest.http(Method.DELETE, `fragment/${fragmentId}`);
  }

  saveSuggestion(suggestion: M.Suggestion): Promise<M.Suggestion> {
    return this.rest.http(Method.POST, 'suggestion/save', suggestion);
  }

  labels(): Promise<M.Label[]> {
    return this.rest.http(Method.GET, 'label');
  }

  saveLabel(label: M.Label): Promise<M.Label> {
    return this.rest.http(Method.POST, 'label', label);
  }

  labelEdges(): Promise<M.LabelEdge[]> {
    return this.rest.http(Method.GET, 'label/edge');
  }

  labelUsage(): Promise<Record<number, number>> {
    return this.rest.http(Method.GET, 'label/usage');
  }

  labelTypes(): Promise<M.LabelType[]> {
    return this.rest.http(Method.GET, 'label/type');
  }

  saveLabelType(labelType: M.LabelType): Promise<M.LabelType> {
    return this.rest.http(Method.POST, 'label/type', labelType);
  }

  users(): Promise<M.User[]> {
    return this.rest.http(Method.GET, 'user');
  }

  publishers(): Promise<M.Publisher[]> {
    return this.rest.http(Method.GET, 'document/publisher');
  }

  savePublisher(publisher: Partial<M.Publisher>): Promise<M.Publisher> {
    return this.rest.http(Method.POST, 'document/publisher', publisher);
  }

  getPublisher(id: number): Promise<M.Publisher> {
    return this.rest.http(Method.GET, `document/publisher/${id}`);
  }

  realms(): Promise<M.Realm[]> {
    return this.rest.http(Method.GET, 'realm');
  }

  getRealm(id: number): Promise<M.Realm> {
    return this.rest.http(Method.GET, `realm/${id}`);
  }

  sseSubscriptionUrl(): string {
    return this.rest.getUrl('sse/subscribe');
  }

  search(query: string, entity: string): Promise<M.SearchResult[]> {
    return this.rest.http(Method.POST, `search/query/${entity}`, { query });
  }

  semanticSearch(query: string): Promise<M.SearchResult[]> {
    return this.rest.http(Method.POST, 'semantic/search', { query });
  }

  semanticSearchExpand(query: M.SemanticSearchRequest): Promise<M.SemanticSearchResult[]> {
    return this.rest.http(Method.POST, 'semantic/expand', query);
  }

  uiConfig(): Promise<M.UiConfig> {
    return this.rest.http(Method.GET, 'ui/config');
  }

  private upload(uri: string, files: File[]): Promise<any> {
    const form = new FormData();
    files.forEach((file) => {
      form.append('file', file);
      form.append('name', file.name);
      form.append('size', `${file.size}`);
    });
    return this.rest.http(Method.POST, uri, form);
  }
}

export enum Method {
  GET,
  POST,
  PUT,
  DELETE,
}
export interface RestInterface {
  http(method: Method, action: string, payload?: any, options?: Partial<RequestInit>): Promise<any>;
  getUrl(path: string): string;
  getRootUrl(path: string): string;
}
export class ApiError extends Error {
  public readonly status: number;

  public readonly statusText: string;

  public readonly code: string;

  public readonly message: string;

  constructor(
    status: number,
    statusText: string,
    code: string = status.toString(),
    message = `http/${status}: ${code} ${statusText}`,
    stack?: string,
  ) {
    super(`http/${status}: ${code} ${message}`);
    this.stack = stack || (new Error()).stack;
    this.status = status;
    this.statusText = statusText;
    this.code = code;
    this.message = message;
  }
}

export class RestInterfaceImpl implements RestInterface {
  private csrfToken = '';

  private tokenPromise: Promise<string> | undefined = undefined;

  constructor(private readonly restOptions: RestOptions) {
  }

  public http(method: Method, action: string, payload?: any, options?: Partial<RequestInit>): Promise<any> {
    const url = this.restOptions.url(action);
    // if (method !== Method.GET && this.csrfToken === '') {
    //   return this.getToken()
    //     .then(() => this.http(method, action, payload, options))
    //     .catch((e) => { throw new Error(`csrf: ${e.message}`); });
    // }
    const callStack = (new Error()).stack;
    return fetch(url, this.request(method, payload, options))
      .catch((e) => {
        this.csrfToken = '';
        throw new Error(`Unable to ${Method[method].toString()} ${url} - ${e}`);
      })
      .then(async (r) => {
        const contentType = r.headers.get('Content-Type');
        if (r.ok) {
          if (r.status === 204) {
            return Promise.resolve(); // no content
          } if (contentType && binaryContentTypes.includes(contentType)) {
            const blob = await r.blob();
            return new Blob([blob], { type: contentType });
          } if (contentType === MediaType.JSON) {
            return r.json();
          }
          return r.text();
        }
        let error: ApiError;
        if (contentType === MediaType.JSON) {
          const message = await r.json();
          error = new ApiError(r.status, r.statusText,
            message.error ? message.error.code : null,
            message.error ? message.error.message : null,
            callStack);
        } else {
          error = new ApiError(r.status, r.statusText, undefined, undefined, callStack);
        }
        if (this.restOptions.errorHandlers[r.status]) {
          await this.restOptions.errorHandlers[r.status]({ method, action }, error);
        }
        throw error;
      });
  }

  public getUrl(action: string): string {
    return this.restOptions.url(action);
  }

  public getRootUrl(action: string): string {
    return this.restOptions.root(action);
  }

  private request(method: Method, payload?: any, options: Partial<RequestInit> = {}): RequestInit {
    const req: RequestInit = deepMerge(this.restOptions.options(), options);
    req.method = Method[method].toString();
    let headers = req.headers as Record<string, string>;
    if (!headers) {
      headers = {};
    }
    if (payload instanceof FormData) {
      delete headers['Content-Type'];
    }
    if (method !== Method.GET) {
      headers['Csrf-Token'] = this.csrfToken;
      if (headers['Content-Type'] === MediaType.JSON) {
        if (payload !== undefined) {
          req.body = JSON.stringify(payload);
        } else {
          headers = { ...headers, 'Content-Type': undefined } as any;
        }
      } else if (payload) {
        req.body = payload;
      }
    }
    // cleanup headers with undefined value
    Object.keys(headers).forEach((key) => !headers[key] && delete headers[key]);
    req.headers = headers;
    return req;
  }

  private getToken(): Promise<string> {
    if (this.csrfToken) {
      return Promise.resolve(this.csrfToken);
    }
    if (this.tokenPromise !== undefined) {
      return this.tokenPromise;
    }
    this.tokenPromise = fetch(
      this.restOptions.url('csrf'), this.request(Method.GET, undefined, { headers: { Accept: '*/*' } }),
    )
      .then((r) => {
        if (r.ok) {
          return r.json();
        }
        throw new ApiError(r.status, r.statusText, r.status.toString(), r.statusText);
      })
      .then((json) => {
        this.csrfToken = json.result;
        return this.csrfToken;
      })
      .then(
        (x) => { this.tokenPromise = undefined; return x; },
        (e) => { this.tokenPromise = undefined; throw e; },
      );
    return this.tokenPromise;
  }
}
interface ErrorHandlers {
  [n: number]: (request: {method: Method; action: string}, error: ApiError) =>
    Promise<void>;
}
interface RestOptions {
  readonly errorHandlers: ErrorHandlers;
  url(path: string): string;
  root(path: string): string;
  options(): RequestInit;
}

const api: Api = new ApiImpl(createRestInterface());
export default api;
